import { SafeError, SafePromise } from '@benzinga/safe-await';
import { ExtendedListenableSubscribable } from '@benzinga/subscribable';

import {
  Logo,
  Schedule,
  ShortInterestParams,
  ShortInterestResponse,
  ShortInterestsDataSet,
  TickerDetailsResponse,
  DetailedQuotesBySymbol,
  GetQuotesLogoParams,
  DelayedQuote,
  TickerDetail,
  BullBearStatements,
  QuoteAnalysisResponse,
  PeerQuotesResponse,
} from './entities';
import {
  QuotesBullSayBearSayRestful,
  QuotesDelayedRestful,
  QuotesInterestRestful,
  QuotesLogosRestful,
  QuotesPeerQuotesRestful,
  QuotesScheduleRestful,
  QuotesSymbolsRestful,
  QuotesTickersRestful,
} from './restful';
import { Session } from '@benzinga/session';
import { StockSymbol } from '@benzinga/session';
import { isArray, chunkifyArray } from '@benzinga/utils';
import { QuoteAnalysisRestful } from './restful/analysis';

interface ErrorEvent {
  error?: SafeError;
  errorType:
    | 'get_detailed_quotes_error'
    | 'get_logos_error'
    | 'get_delayed_quotes_error'
    | 'get_ticker_details_error'
    | 'get_short_interest_error';
  type: 'error';
}

interface WatchlistQuotesEvent {
  type: 'get_detailed_quotes';
  watchlistQuotes: DetailedQuotesBySymbol;
}

interface ShortInterestEvent {
  type: 'get_short_interest';
  shortInterest: ShortInterestResponse;
}

interface LogoQuotesEvent {
  logos: Logo[];
  type: 'get_quotes_logos';
}

interface DelayedQuotesEvent {
  quotes: Record<StockSymbol, DelayedQuote>;
  type: 'get_delayed_quotes';
}
interface TickerDetailsEvent {
  result: TickerDetail[];
  type: 'get_ticker_details';
}

export type WatchlistQuotesRequestEvent =
  | ErrorEvent
  | WatchlistQuotesEvent
  | LogoQuotesEvent
  | DelayedQuotesEvent
  | TickerDetailsEvent
  | ShortInterestEvent;

interface WatchlistQuotesFunctions {
  getDelayedQuotes: WatchlistQuotesRequest['getDelayedQuotes'];
  getDetailedQuotes: WatchlistQuotesRequest['getDetailedQuotes'];
  getQuotesLogos: WatchlistQuotesRequest['getQuotesLogos'];
  getShortInterest: WatchlistQuotesRequest['getShortInterest'];
  getTickerDetails: WatchlistQuotesRequest['getTickerDetails'];
}

export class WatchlistQuotesRequest extends ExtendedListenableSubscribable<
  WatchlistQuotesRequestEvent,
  WatchlistQuotesFunctions
> {
  private scheduleRestful: QuotesScheduleRestful;
  private delayedRestful: QuotesDelayedRestful;
  private tickerRestful: QuotesTickersRestful;
  private interestRestful: QuotesInterestRestful;
  private detailedQuotesRestful: QuotesSymbolsRestful;
  private logosRestful: QuotesLogosRestful;
  private bullSayBearSayRestful: QuotesBullSayBearSayRestful;
  private analysisRestful: QuoteAnalysisRestful;
  private peerQuotesRestful: QuotesPeerQuotesRestful;

  constructor(session: Session) {
    super();
    this.scheduleRestful = new QuotesScheduleRestful(session);
    this.delayedRestful = new QuotesDelayedRestful(session);
    this.detailedQuotesRestful = new QuotesSymbolsRestful(session);
    this.tickerRestful = new QuotesTickersRestful(session);
    this.interestRestful = new QuotesInterestRestful(session);
    this.logosRestful = new QuotesLogosRestful(session);
    this.bullSayBearSayRestful = new QuotesBullSayBearSayRestful(session);
    this.analysisRestful = new QuoteAnalysisRestful(session);
    this.peerQuotesRestful = new QuotesPeerQuotesRestful(session);
  }

  public getDetailedQuotes = async (symbols: StockSymbol[]): SafePromise<DetailedQuotesBySymbol> => {
    const watchlistQuotes = await this.detailedQuotesRestful.getDetailedQuotes(symbols.toString());

    if (watchlistQuotes.err) {
      this.dispatch({
        error: watchlistQuotes.err,
        errorType: 'get_detailed_quotes_error',
        type: 'error',
      });
    } else {
      this.dispatch({
        type: 'get_detailed_quotes',
        watchlistQuotes: watchlistQuotes.ok,
      });
    }
    return watchlistQuotes;
  };

  public getQuotesLogos = async (
    symbols: StockSymbol[],
    params?: GetQuotesLogoParams,
  ): SafePromise<{ data: Logo[] }> => {
    const logos = await this.logosRestful.getQuotesLogos(symbols.toString(), params);

    if (logos.err) {
      this.dispatch({
        error: logos.err,
        errorType: 'get_logos_error',
        type: 'error',
      });
      return logos;
    } else {
      this.dispatch({
        logos: logos.ok.data,
        type: 'get_quotes_logos',
      });
      return logos;
    }
  };

  public getDelayedQuotes = async (symbols: StockSymbol[]): SafePromise<Record<StockSymbol, DelayedQuote>> => {
    const MAX_SYMBOLS_BATCH_SIZE = 100;
    const symbolsCopy = [...symbols];
    const chunkedSymbols = chunkifyArray(symbolsCopy, MAX_SYMBOLS_BATCH_SIZE);

    const results = await Promise.all(
      chunkedSymbols.map(chunk => this.delayedRestful.getDelayedQuotes(chunk.toString())),
    );

    const firstError = results.find(result => result.err);
    if (firstError?.err) {
      this.dispatch({
        error: firstError.err,
        errorType: 'get_delayed_quotes_error',
        type: 'error',
      });
      return { err: firstError.err };
    }

    const mergedQuotes = results.reduce<Record<StockSymbol, DelayedQuote>>((acc, result) => {
      if (result.ok) {
        return { ...acc, ...result.ok };
      }
      return acc;
    }, {});

    this.dispatch({
      quotes: mergedQuotes,
      type: 'get_delayed_quotes',
    });

    return { ok: mergedQuotes };
  };

  public getTickerDetails = async (symbols: StockSymbol[]): SafePromise<TickerDetailsResponse> => {
    const tickerDetails = await this.tickerRestful.getTickerDetails(symbols.toString());

    if (tickerDetails.err) {
      this.dispatch({
        error: tickerDetails.err,
        errorType: 'get_ticker_details_error',
        type: 'error',
      });
      return { err: tickerDetails.err };
    } else {
      this.dispatch({
        result: tickerDetails.ok.result,
        type: 'get_ticker_details',
      });

      return tickerDetails;
    }
  };

  public getSchedule = async (): SafePromise<Schedule> => {
    return await this.scheduleRestful.getSchedule();
  };

  public getShortInterest = async (params: ShortInterestParams): SafePromise<ShortInterestsDataSet> => {
    const shortInterests = await this.interestRestful.getShortInterests(params);

    if (shortInterests.err) {
      this.dispatch({
        error: shortInterests.err,
        errorType: 'get_short_interest_error',
        type: 'error',
      });

      return { err: shortInterests.err };
    } else {
      this.dispatch({
        shortInterest: shortInterests.ok,
        type: 'get_short_interest',
      });
    }

    return { ok: shortInterests?.ok?.shortInterestData };
  };

  public getBullSayBearSayStatements = async (symbols: string[]): SafePromise<BullBearStatements[]> => {
    const statements = await this.bullSayBearSayRestful.getStatements(symbols);
    if (statements.err) {
      return { err: statements.err };
    } else if (isArray(statements.ok)) {
      return { err: new SafeError('No results', 'No results') };
    } else {
      return { ok: statements.ok.bulls_say_bears_say };
    }
  };

  public getQuoteAnalysis = async (symbol: string): SafePromise<QuoteAnalysisResponse> => {
    return this.analysisRestful.getQuoteAnalysis(symbol);
  };

  public getPeerQuotes = async (symbol: StockSymbol, limit?: number): SafePromise<PeerQuotesResponse> => {
    return this.peerQuotesRestful.getPeerQuotes(symbol, limit);
  };

  protected onSubscribe(): WatchlistQuotesFunctions {
    return {
      getDelayedQuotes: this.getDelayedQuotes,
      getDetailedQuotes: this.getDetailedQuotes,
      getQuotesLogos: this.getQuotesLogos,
      getShortInterest: this.getShortInterest,
      getTickerDetails: this.getTickerDetails,
    };
  }
}
